<template>
  <div class="about-page">

    <!-- ── Hero ──────────────────────────────────────── -->
    <section class="about-hero">
      <div class="about-hero__bg"></div>
      <div class="about-hero__overlay"></div>
      <div class="about-hero__content">
        <span class="about-hero__eyebrow">Golden Key Portal</span>
        <h1 class="about-hero__title">Sobre Nós</h1>
        <p class="about-hero__subtitle">
          O seu portal de confiança em Moçambique
        </p>
      </div>
    </section>

    <!-- ── Who We Are ────────────────────────────────── -->
    <section class="section-pad">
      <div class="container">
        <div class="section-label">Nossa História</div>
        <h2 class="section-heading">Quem Somos</h2>

        <div class="who-grid">
          <div class="who-grid__quote">
            <div class="who-grid__quote-mark">"</div>
            <p class="who-grid__pull">
              Tecnologia e experiência humana a serviço do mercado imobiliário moçambicano.
            </p>
            <div class="who-grid__rule"></div>
          </div>

          <div class="who-grid__body">
            <p class="body-text q-mb-lg">
              A <strong>Golden Key</strong> é um portal inovador que combina tecnologia e
              experiência humana para revolucionar o mercado imobiliário em Moçambique. A nossa
              plataforma oferece uma solução ágil, transparente e segura para compradores,
              vendedores, inquilinos e corretores.
            </p>
            <p class="body-text body-text--muted">
              A nossa missão é tornar o processo de transação imobiliária mais simples, acessível
              e eficiente — conectando pessoas aos imóveis certos de forma digital, sem abrir mão
              do atendimento personalizado.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Vision ────────────────────────────────────── -->
    <section class="vision-section">
      <div class="container">
        <div class="vision-inner">
          <div class="section-label section-label--light">Nossa Visão</div>
          <blockquote class="vision-quote">
            Ser a principal referência em tecnologia imobiliária em
            <em>África</em>, tornando o mercado mais digital, acessível e
            confiável — proporcionando experiências positivas para todos os
            envolvidos nas transações imobiliárias.
          </blockquote>
          <div class="vision-rule"></div>
        </div>
      </div>
    </section>

    <!-- ── Values ────────────────────────────────────── -->
    <section class="section-pad bg-surface">
      <div class="container">
        <div class="section-label">Princípios</div>
        <h2 class="section-heading">Os Nossos Valores</h2>
        <p class="section-sub">Os pilares que guiam cada decisão que tomamos</p>

        <div class="values-grid">
          <div
            v-for="(value, i) in values"
            :key="i"
            class="value-card"
          >
            <div class="value-card__icon" :style="`--accent: ${value.color}`">
              <q-icon :name="value.icon" size="26px" />
            </div>
            <h3 class="value-card__title">{{ value.title }}</h3>
            <p class="value-card__desc">{{ value.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Contact ───────────────────────────────────── -->
    <section class="section-pad">
      <div class="container">
        <div class="section-label">Contacto</div>
        <h2 class="section-heading">Fale Connosco</h2>
        <p class="section-sub">Estamos prontos para te ajudar a encontrar o imóvel ideal</p>

        <div class="contact-grid">
          <button class="contact-card" @click="contact('email')">
            <div class="contact-card__icon contact-card__icon--blue">
              <q-icon name="email" size="28px" />
            </div>
            <div class="contact-card__label">Email</div>
            <div class="contact-card__title">Envie uma mensagem</div>
            <div class="contact-card__value">comercial@goldenkey.co.mz</div>
          </button>

          <button class="contact-card" @click="contact('phone')">
            <div class="contact-card__icon contact-card__icon--blue">
              <q-icon name="phone" size="28px" />
            </div>
            <div class="contact-card__label">Telefone</div>
            <div class="contact-card__title">Ligue para nós</div>
            <div class="contact-card__value">+258 82 701 6716</div>
          </button>

          <button class="contact-card" @click="contact('whatsapp')">
            <div class="contact-card__icon contact-card__icon--green">
              <q-icon name="chat" size="28px" />
            </div>
            <div class="contact-card__label">WhatsApp</div>
            <div class="contact-card__title">Chat direto</div>
            <div class="contact-card__value">+258 82 701 6716</div>
          </button>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default { name: 'AboutUs' };
</script>

<script setup>
import { ref, onMounted } from 'vue';
import { useSeoMeta } from '@unhead/vue';

onMounted(() => { window.scrollTo(0, 0); });

useSeoMeta({
  title: 'Sobre Nós - Golden Key',
  description: 'Seu portal de confiança. Encontre o imóvel dos seus sonhos com as melhores condições do mercado.',
  ogTitle: 'Sobre Nós - Golden Key',
  ogDescription: 'Seu portal de confiança. Encontre o imóvel dos seus sonhos com as melhores condições do mercado.',
  ogImage: 'https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png',
});

const values = ref([
  {
    title: 'Transparência',
    icon: 'verified_user',
    color: '#027BE3',
    description: 'Informações claras, verídicas e completas sobre todos os imóveis e processos de negociação.'
  },
  {
    title: 'Inovação',
    icon: 'psychology',
    color: '#EBA301',
    description: 'Tecnologia contínua para melhorar a experiência do utilizador e tornar a jornada imobiliária mais eficiente.'
  },
  {
    title: 'Confiança',
    icon: 'favorite',
    color: '#027BE3',
    description: 'Relacionamentos baseados em credibilidade, integridade e compromisso com a satisfação do cliente.'
  },
  {
    title: 'Acessibilidade',
    icon: 'accessibility_new',
    color: '#21BA45',
    description: 'Ferramentas fáceis de usar para todos — desde iniciantes até profissionais experientes do setor.'
  },
  {
    title: 'Sustentabilidade',
    icon: 'forest',
    color: '#EBA301',
    description: 'Apoio a práticas responsáveis de desenvolvimento urbano e consumo consciente no setor imobiliário.'
  },
  {
    title: 'Parceria',
    icon: 'handshake',
    color: '#027BE3',
    description: 'Colaboração com corretores, proprietários e clientes para criar valor mútuo e soluções duradouras.'
  }
]);

function contact(method) {
  const urls = {
    email:    'mailto:comercial@goldenkey.co.mz',
    phone:    'tel:+258827016716',
    whatsapp: 'https://wa.me/258827016716',
  };
  window.open(urls[method], '_blank');
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');

/* ══════════════════════════════════════════════════
   Base
══════════════════════════════════════════════════ */
.about-page {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.section-pad {
  padding: 6rem 0;
}

.bg-surface { background: #f8fafc; }

/* ── Section typography ─────────────────── */
.section-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #EBA301;
  margin-bottom: 0.75rem;
}

.section-label--light { color: rgba(235, 163, 1, 0.85); }

.section-heading {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 300;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0 0 0.5rem;
}

.section-sub {
  font-size: 1rem;
  font-weight: 400;
  color: #64748b;
  margin: 0 0 3.5rem;
  line-height: 1.6;
}

/* ══════════════════════════════════════════════════
   Hero
══════════════════════════════════════════════════ */
.about-hero {
  position: relative;
  width: 100%;
  height: 48vh;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.about-hero__bg {
  position: absolute;
  inset: 0;
  background-image: url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80');
  background-size: cover;
  background-position: center 40%;
}

.about-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(8, 12, 24, 0.88) 0%,
    rgba(8, 12, 24, 0.55) 40%,
    rgba(8, 12, 24, 0.30) 100%
  );
}

.about-hero__content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 2rem;
  animation: fadeUp 0.9s ease-out both;
}

.about-hero__eyebrow {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #EBA301;
  margin-bottom: 1rem;
}

.about-hero__title {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 300;
  color: #ffffff;
  letter-spacing: 0.04em;
  line-height: 1.1;
  margin: 0 0 1rem;
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
}

.about-hero__subtitle {
  font-size: 1rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.04em;
  margin: 0;
}

/* ══════════════════════════════════════════════════
   Who We Are
══════════════════════════════════════════════════ */
.who-grid {
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  gap: 5rem;
  align-items: start;
  padding-top: 1rem;
}

.who-grid__quote {
  position: relative;
}

.who-grid__quote-mark {
  font-family: Georgia, serif;
  font-size: 7rem;
  line-height: 0.7;
  color: #EBA301;
  opacity: 0.25;
  margin-bottom: 1rem;
  user-select: none;
}

.who-grid__pull {
  font-size: 1.25rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.65;
  margin: 0 0 2rem;
  padding-left: 1.25rem;
  border-left: 3px solid #EBA301;
}

.who-grid__rule {
  width: 48px;
  height: 2px;
  background: #EBA301;
  opacity: 0.4;
  margin-left: 1.25rem;
}

.body-text {
  font-size: 1rem;
  font-weight: 400;
  color: #334155;
  line-height: 1.85;
  margin: 0;
}

.body-text--muted { color: #64748b; }

/* ══════════════════════════════════════════════════
   Vision
══════════════════════════════════════════════════ */
.vision-section {
  background: #0a0e1a;
  padding: 6rem 0;
}

.vision-inner {
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
  padding: 0 2rem;
}

.vision-quote {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.7;
  letter-spacing: 0.01em;
  margin: 1.5rem 0 2.5rem;
  font-style: normal;
}

.vision-quote em {
  color: #EBA301;
  font-style: normal;
  font-weight: 500;
}

.vision-rule {
  width: 56px;
  height: 2px;
  background: #EBA301;
  opacity: 0.55;
  margin: 0 auto;
}

/* ══════════════════════════════════════════════════
   Values
══════════════════════════════════════════════════ */
.values-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.value-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  padding: 2rem 1.75rem;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.value-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.09);
  border-color: rgba(0, 0, 0, 0.12);
}

.value-card__icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent) 12%, transparent);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  transition: transform 0.25s ease;
}

.value-card:hover .value-card__icon {
  transform: scale(1.08);
}

.value-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
  margin: 0 0 0.6rem;
}

.value-card__desc {
  font-size: 0.875rem;
  font-weight: 400;
  color: #64748b;
  line-height: 1.7;
  margin: 0;
}

/* ══════════════════════════════════════════════════
   Contact
══════════════════════════════════════════════════ */
.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.contact-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  padding: 2.5rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  width: 100%;
}

.contact-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.10);
  border-color: #027BE3;
}

.contact-card__icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
}

.contact-card__icon--blue {
  background: rgba(2, 123, 227, 0.09);
  color: #027BE3;
}

.contact-card__icon--green {
  background: rgba(33, 186, 69, 0.09);
  color: #21BA45;
}

.contact-card__label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.5rem;
}

.contact-card__title {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.4rem;
}

.contact-card__value {
  font-size: 0.9rem;
  font-weight: 500;
  color: #027BE3;
}

/* ══════════════════════════════════════════════════
   Animation
══════════════════════════════════════════════════ */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0);    }
}

/* ══════════════════════════════════════════════════
   Responsive
══════════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .values-grid,
  .contact-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .section-pad { padding: 4rem 0; }

  .about-hero {
    height: 42vh;
    min-height: 300px;
  }

  .who-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .who-grid__quote-mark { font-size: 5rem; }
  .who-grid__pull       { font-size: 1.1rem; }

  .values-grid,
  .contact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
